<template>
  <div id="popups">
    <!-- Content -->
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader class="d-flex justify-content-between align-items-center py-3">
            <h4 class="mb-0">
              Publicações
            </h4>

            <div class="d-flex">
              <router-link
                to="/publications/create"
                class="btn btn-primary d-flex align-items-center"
              >
                <CIcon
                  name="cil-library-add"
                  class="mr-2"
                />
                Incluir
              </router-link>
            </div>
          </CCardHeader>
          <CCardBody>
            <!-- Search -->
            <CRow>
              <CCol col="12">
                <Search @search="updateSearch" />
              </CCol>
            </CRow>
            <CRow>
              <CCol col="12">
                <CRow>
                  <!-- Status -->
                  <CCol
                    md="4"
                    lg="4"
                    xl="3"
                  >
                    <div class="form-group">
                      <Multiselect
                        :value="statusOptions.find(option => option.value == query.status)"
                        :options="statusOptions"
                        label="label"
                        track-by="value"
                        placeholder="Status"
                        @select="(selected) => query.status = selected.value"
                      />
                    </div>
                  </CCol>

                  <!-- Sections -->
                  <CCol
                    md="4"
                    lg="4"
                    xl="3"
                  >
                    <div class="form-group">
                      <Multiselect
                        :value="sectionsOptions.find(option => option.value == query.section)"
                        :options="sectionsOptions"
                        label="label"
                        track-by="value"
                        placeholder="Solução"
                        @select="(selected) => query.section = selected.value"
                      />
                    </div>
                  </CCol>
                </CRow>
              </CCol>
            </CRow>
            <!-- Datatable -->
            <CRow>
              <CCol col="12">
                <DataTable
                  :sorter="query.sorter"
                  :items="datatable.items"
                  :fields="datatable.fields"
                  :enable-export="false"
                  base-route="/publications/edit/"
                  @sorterValue="(event) => (query.sorter = event)"
                  @row-clicked="handleRowClick"
                  @update:checked="updateStatus"
                >
                  <template #actions="{ id }">
                    <DatatableEdit :url="`/publications/edit/${id}`" />
                    <DatatableRemove
                      @row-clicked="handleRowClick"
                      @deleted="deleteItem"
                    />
                  </template>
                </DataTable>
              </CCol>
            </CRow>

            <!-- Pagination -->
            <CRow class="mt-2">
              <CCol lg="12">
                <Pagination
                  :page="query.page"
                  :per-page="query.perpage"
                  :pages="pagination.pages"
                  :total="pagination.total"
                  :items="pagination.items"
                  @update:active-page="(page) => (query.page = page)"
                  @update:perpage="(perpage) => (query.perpage = perpage)"
                />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
// Components
import DataTable from '@/components/ui/Datatable'
import DatatableEdit from '@/components/ui/DatatableActions/Edit'
import DatatableRemove from '@/components/ui/DatatableActions/Remove'
import Pagination from '@/components/ui/Pagination'
import Search from '@/components/ui/Search'
import Multiselect from '@/components/ui/Multiselect'

// Services
import Service from '@/services/publication.service'
import ChapterService from '@/services/chapter.service'

export default {
  metaInfo: {
    title: 'Publicações',
    titleTemplate: ' %s - Manager - Intelli'
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (!vm.$store.state.auth.permissions.includes('manage_publications')) {
        vm.$router.push('/404')
      }
    })
  },

  components: {
    DataTable,
    DatatableEdit,
    Pagination,
    Search,
    Multiselect,
    DatatableRemove
  },

  data() {
    return {
      datatable: {
        items: [],
        fields: [
          {
            key: 'status',
            label: 'Status',
            _style: 'width: 5%; min-width: 0px;',
            sorter: false
          },
          {
            key: 'title',
            label: 'Título',
            _style: 'width: 40%;',
            sorter: true
          },
          {
            key: 'section',
            label: 'Seção',
            sorter: false
          },
          {
            key: 'language',
            label: 'Idiomas',
            sorter: false
          },
          {
            key: 'actions',
            label: 'Ações',
            sorter: false,
            _style: 'width: 12%; min-width: 0px'
          }
        ]
      },
      query: {
        search: '',
        page: 1,
        perpage: localStorage.getItem('perPageItem')
          ? parseFloat(localStorage.getItem('perPageItem'))
          : 10,
        pagination: true,
        status: null,
        section: null,
        sorter: {
          asc: false,
          column: ''
        }
      },
      pagination: {
        total: 0,
        pages: 0,
        items: 0
      },
      statusOptions: [
        {
          label: 'Todos os status',
          value: null

        },
        {
          label: 'Ativo',
          value: true
        },
        {
          label: 'Inativo',
          value: false
        }
      ],
      sectionsOptions: [
        {
          label: 'Todos os seções',
          value: null

        }
      ],
      rowCliked: {}
    }
  },

  watch: {
    query: {
      handler: 'updateDataTable',
      deep: true
    }
  },

  async created() {
    await this.getSections()
    await this.updateDataTable()
  },

  methods: {
    async updateDataTable() {
      const response = await Service.index(this.query)
      const { data, pagination } = response

      if (data) {
        this.pagination = {
          ...pagination,
          pages: pagination.lastPage,
          items: data.length
        }

        const newItems = response.data.map((item) => {
          return {
            ...item,
            section: item.section ? item.section.title : '-'
          }
        })

        this.datatable.items = newItems
      }
    },

    async getSections() {
      const response = await ChapterService.index({ pagination: false, sorter: { column: 'title', asc: true } })

      const options = response.data.map(item => {
        return {
          value: item.id,
          label: item.title
        }
      })

      this.sectionsOptions = [...this.sectionsOptions, ...options]
    },

    async updateSearch(searchText) {
      this.query.search = searchText
      this.query.page = 1

      await this.updateDataTable()
    },

    handleRowClick(event) {
      this.rowCliked = event
    },

    async updateStatus(event) {
      const response = await Service.updateStatus(this.rowCliked, event)

      if (response.status === 200) {
        this.$notify({
          group: 'notifications',
          type: 'success',
          text: 'Status alterado com sucesso'
        })
        await this.updateDataTable()
      } else {
        this.$notify({
          group: 'notifications',
          type: 'error',
          text: 'Ocorreu um erro ao atualizar o status, por favor tente novamente!'
        })
      }
    },

    async deleteItem() {
      const response = await Service.delete(this.rowCliked)

      if (response.status === 200) {
        this.$notify({
          group: 'notifications',
          type: 'success',
          text: 'Publicação excluída com sucesso!'
        })
      } else {
        this.$notify({
          group: 'notifications',
          type: 'error',
          text: 'Não foi possivel excluir a publicação, por favor tente novamente'
        })
      }

      await this.updateDataTable()
    }
  }
}
</script>

<style lang="scss">
</style>
